import { useState, useEffect } from "react";
import { socket } from "./socket";
import ReactGA from "react-ga4";
import ConnectionManager from "./components/ConnectionManager";
import GameType from "./models/game";
import PlayerType from "./models/player";
import MessageType from "./models/message";
import ChangeSuiteForm from "./components/ChangeSuitForm";
import GameBoard from "./components/GameBoard";
import Modal from "./components/Modal";
import ChallengeCard from "./components/ChallengeCard";
import Congratulations from "./components/Congratulations";
import Credits from "./components/Credits";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import {
  login,
  assignRoom,
  assignRoomId,
  userNameIsNotUnique,
  userNameIsUnique,
  setPlayer,
} from "./features/user/userSlice";
import {
  updateGame,
  setWinner,
  cardChallengeAccepted,
  pickACard,
  resetGame,
} from "./features/game/gameSlice";
import i18n from "./i18n";
import { useTranslation } from "react-i18next";

import "./App.css";

function App() {
  const [message, setMessage] = useState<MessageType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const roomId = useAppSelector((state) => state.user.roomId);
  const isLoggedIn = useAppSelector((state) => state.user.isLoggedIn);
  const player = useAppSelector((state) => state.user.player);
  const game = useAppSelector((state) => state.game.game);
  const winner = useAppSelector((state) => state.game.winner);

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage(navigator.language);
    ReactGA.initialize("G-76BL7JNRND");
    ReactGA.send({ hitType: "pageview", page: window.location.pathname });
  }, []);

  useEffect(() => {
    document.title = t("App.title");
  }, [t]);

  useEffect(() => {
    function onUsernameTaken() {
      dispatch(userNameIsNotUnique());
    }

    function onRoomCreated(gameState: GameType, id: string) {
      dispatch(updateGame(gameState));
      dispatch(assignRoomId(id));
    }

    function onRoomAssigned(id: string) {
      dispatch(assignRoom());
      dispatch(assignRoomId(id));
    }

    function onAfterLogin(gameState: GameType, newPlayer: PlayerType) {
      dispatch(updateGame(gameState));
      dispatch(userNameIsUnique());
      dispatch(login());
      dispatch(setPlayer(newPlayer));
    }

    function onLooseTurn(gameState: GameType, thisPlayer: PlayerType) {
      dispatch(updateGame(gameState));
      dispatch(setPlayer(thisPlayer));
    }

    function onCardNotAllowed() {
      setMessage({
        title: t("ModalMessage.warning"),
        type: "warning",
        content: t("ModalMessage.cardNotAllowed"),
      });
      setIsModalOpen(true);
    }

    function onUserDisconnected(gameState: GameType, username: string) {
      setMessage({
        title: t("ModalMessage.warning"),
        type: "warning",
        content: t("ModalMessage.playerLeaves", { username }),
      });
      dispatch(updateGame(gameState));
      setIsModalOpen(true);
    }

    socket.on("username taken", onUsernameTaken);
    socket.on("room created", onRoomCreated);
    socket.on("room assigned", onRoomAssigned);
    socket.on("after login", onAfterLogin);
    socket.on("loose turn", onLooseTurn);
    socket.on("card not allowed", onCardNotAllowed);
    socket.on("user disconnected", onUserDisconnected);

    return () => {
      socket.disconnect();
      socket.off("username taken", onUsernameTaken);
      socket.off("room created", onRoomCreated);
      socket.off("room assigned", onRoomAssigned);
      socket.off("after login", onAfterLogin);
      socket.off("loose turn", onLooseTurn);
      socket.off("card not allowed", onCardNotAllowed);
      socket.off("user disconnected", onUserDisconnected);
    };
  }, [dispatch, t]);

  useEffect(() => {
    function onUpdateGame(gameState: GameType, extraAction?: string) {
      dispatch(updateGame(gameState));

      switch (extraAction) {
        case "direction changed":
          setMessage({
            title: t("ModalMessage.warning"),
            type: "warning",
            content: t("ModalMessage.directionChanged"),
          });
          setIsModalOpen(true);
          break;
        case "reset":
          // dispatch(setWinner(null));
          dispatch(resetGame());
          const updatedPlayer = gameState.players.find(
            (gamer) => gamer.id === player.id,
          );
          if (updatedPlayer) {
            dispatch(setPlayer(updatedPlayer));
          }
          break;
        case "suite changed":
          setMessage({
            title: t("ModalMessage.warning"),
            type: "warning",
            content: t("ModalMessage.suitChanged"),
          });
          setIsModalOpen(true);
          break;
      }
    }

    socket.on("update game", onUpdateGame);

    return () => {
      socket.off("update game", onUpdateGame);
    };
  }, [dispatch, t, player.id]);

  useEffect(() => {
    function onGainTurn(
      gameState: GameType,
      thisPlayer: PlayerType,
      extraAction?: string,
    ) {
      dispatch(updateGame(gameState));
      dispatch(setPlayer(thisPlayer));
      const topCard = gameState.discardPile.at(-1);

      if (typeof topCard == "undefined") {
        // This should never happend
        throw new Error("The topcard is undefined");
      }

      switch (extraAction) {
        case "challenge card":
          setMessage({
            title: t("ModalMessage.challengeCard"),
            type: "warning",
            hideCloseButton: true,
            content: (
              <ChallengeCard
                card={topCard}
                handleSubmit={(challengeAccepted: boolean) => {
                  setIsModalOpen(false);
                  if (challengeAccepted) {
                    // TODO: lock cards that can not be played, then let the user play a card
                    dispatch(cardChallengeAccepted());
                  } else {
                    socket.emit("get penalty cards", roomId);
                  }
                }}
              />
            ),
          });
          setIsModalOpen(true);
          break;
        case "reset":
          dispatch(resetGame());
          break;
      }

      let canPlayACard = false;

      thisPlayer.hand.forEach((card) => {
        if (
          card.action === "draw-4" ||
          card.action === "wild" ||
          card.face === topCard.face ||
          card.suit === topCard.suit ||
          card.suit === gameState.tempSuit
        ) {
          canPlayACard = true;
        }
      });

      if (canPlayACard) {
        if (extraAction === "suit changed") {
          setMessage({
            title: t("ModalMessage.warning"),
            type: "warning",
            content: t("ModalMessage.suitChanged"),
          });
          setIsModalOpen(true);
        }
      } else {
        /*
        setMessage({
          title: "Warning",
          type: "warning",
          content: "You don't have any card to play, please pick one.",
        });
        setIsModalOpen(true);
        */
        dispatch(pickACard());
      }
    }

    function onChooseSuit() {
      setMessage({
        title: t("ModalMessage.chooseSuit"),
        type: "warning",
        hideCloseButton: true,
        content: (
          <ChangeSuiteForm
            handleSubmit={(newSuit: string) => {
              setIsModalOpen(false);
              socket.emit("change suit", roomId, newSuit);
            }}
          />
        ),
      });
      setIsModalOpen(true);
    }

    socket.on("gain turn", onGainTurn);
    socket.on("choose suit", onChooseSuit);

    return () => {
      socket.off("gain turn", onGainTurn);
      socket.off("choose suit", onChooseSuit);
    };
  }, [roomId, dispatch, t]);

  useEffect(() => {
    function onGameHasWinner(gameState: GameType, winnerPlayer: PlayerType) {
      // dispatch(updateGame(gameState));
      dispatch(setWinner(winnerPlayer));
      if (player.id === winnerPlayer.id) {
        dispatch(setPlayer(winnerPlayer));
      }
    }

    socket.on("game has winner", onGameHasWinner);

    return () => {
      socket.off("game has winner", onGameHasWinner);
    };
  }, [player, dispatch]);

  // There is not socket
  if (!socket) {
    return (
      <>
        <h1>Error</h1>
        <p>The connection to the game has failed!</p>
      </>
    );
  }

  return (
    <div className="App">
      <ConnectionManager />
      {isLoggedIn && game.state !== "waiting" && <GameBoard />}
      {message && (
        <Modal
          open={isModalOpen}
          title={message.title}
          type={message.type}
          handleDismiss={() => {
            setIsModalOpen(false);
          }}
          hideCloseButton={message.hideCloseButton}
        >
          {message.content}
        </Modal>
      )}
      {winner && <Congratulations winner={winner} />}
      <Credits />
    </div>
  );
}

export default App;
